import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

export default function LandscapingPage() {
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCallbackForm, setShowCallbackForm] = useState(false);
  const [callbackSubmitted, setCallbackSubmitted] = useState(false);
  const [callbackData, setCallbackData] = useState({
    name: '',
    phone: '',
    email: '',
    preferredTime: 'morning',
    message: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessType: 'residential',
    teamSize: '1-2'
  });

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyBar(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);

    const timer = setTimeout(() => {
      if (!formSubmitted) {
        setShowLeadForm(true);
      }
    }, 8000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, [formSubmitted]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('businessType', formData.businessType);
      formBody.append('teamSize', formData.teamSize);

      await fetch('https://readdy.ai/api/form/d69b95vmvg9ih2c79um0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      setFormSubmitted(true);
      setShowLeadForm(false);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCallbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', callbackData.name);
      formBody.append('phone', callbackData.phone);
      formBody.append('email', callbackData.email);
      formBody.append('preferredTime', callbackData.preferredTime);
      formBody.append('message', callbackData.message);

      await fetch('https://readdy.ai/api/form/d69b95vmvg9ih2c79uig', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      setCallbackSubmitted(true);
      setShowCallbackForm(false);
      setTimeout(() => setCallbackSubmitted(false), 5000);
    } catch (error) {
      console.error('Callback form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    {
      icon: 'ri-plant-line',
      title: 'Garden Design',
      description:
        'Generate detailed quotes for complete garden designs including plants, soil, mulch, and installation with seasonal planting guides.',
      price: 'From $2,500'
    },
    {
      icon: 'ri-landscape-line',
      title: 'Hardscaping',
      description:
        'Quote retaining walls, paving, pathways, and outdoor structures with material specifications and engineering requirements.',
      price: 'From $4,800'
    },
    {
      icon: 'ri-drop-line',
      title: 'Irrigation Systems',
      description:
        'Create comprehensive irrigation quotes including sprinklers, drip systems, controllers, and water-efficient solutions.',
      price: 'From $1,800'
    },
    {
      icon: 'ri-scissors-cut-line',
      title: 'Lawn Installation',
      description:
        'Quote turf installation, soil preparation, and lawn establishment with ongoing maintenance recommendations.',
      price: 'From $35/m²'
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Outdoor Lighting',
      description:
        'Generate quotes for garden lighting, pathway lights, feature lighting, and smart outdoor lighting systems.',
      price: 'From $1,200'
    },
    {
      icon: 'ri-recycle-line',
      title: 'Maintenance Plans',
      description:
        'Create recurring maintenance quotes for lawn care, pruning, fertilizing, and seasonal garden care programs.',
      price: 'From $150/visit'
    }
  ];

  const stats = [
    { value: '17+', label: 'Hours Saved Weekly', icon: 'ri-time-line' },
    { value: '3,600+', label: 'Landscapers Using It', icon: 'ri-team-line' },
    { value: '87%', label: 'Quote Win Rate', icon: 'ri-trophy-line' },
    { value: '$14K', label: 'Avg Monthly Revenue Increase', icon: 'ri-money-dollar-circle-line' }
  ];

  const testimonials = [
    {
      name: 'Ryan Cooper',
      business: 'Cooper Landscapes',
      specialty: 'Garden Design',
      image:
        'https://public.readdy.ai/ai/img_res/760c793761457b15b691efff345ce057.jpg',
      quote:
        'The garden design quoting feature is incredible. I can now include plant lists, soil requirements, and installation costs in one professional quote. My clients love the visual presentations.',
      results: 'Increased monthly revenue by $16,000'
    },
    {
      name: 'Michelle Torres',
      business: 'Torres Outdoor Living',
      specialty: 'Hardscaping',
      image:
        'https://readdy.ai/api/search-image?query=professional%20female%20landscaper%20with%20premium%20hardscaping%20materials%20and%20paving%20stones%20wearing%20quality%20work%20clothes%20confident%20pose%20in%20modern%20luxury%20outdoor%20living%20space%20construction%20site%20natural%20daylight%20showing%20expertise%20cinematic%20quality&width=400&height=400&seq=land-test-premium-2&orientation=squarish',
      quote:
        'Quoting hardscape projects used to take forever with all the material calculations. Now the system handles everything automatically including excavation and drainage. Game changer!',
      results: 'Won 15 major hardscape projects in 3 months'
    },
    {
      name: 'Daniel Kim',
      business: 'Kim Commercial Landscapes',
      specialty: 'Commercial Projects',
      image:
        'https://readdy.ai/api/search-image?query=experienced%20landscaper%20on%20commercial%20property%20with%20professional%20landscaping%20equipment%20and%20irrigation%20system%20wearing%20safety%20gear%20confident%20appearance%20modern%20building%20grounds%20natural%20lighting%20showing%20quality%20workmanship%20ultra%20sharp%20cinematic%20photography&width=400&height=400&seq=land-test-premium-3&orientation=squarish',
      quote:
        'Managing multiple commercial landscaping contracts was overwhelming. Now I can generate detailed quotes with maintenance schedules in minutes and track everything in one place.',
      results: 'Secured 8 major commercial contracts'
    }
  ];

  const supplierPricing = [
    {
      category: 'Plants & Trees',
      items: ['Native plants', 'Ornamental trees', 'Shrubs & hedges', 'Ground covers'],
      icon: 'ri-plant-line'
    },
    {
      category: 'Hardscape Materials',
      items: ['Pavers & stones', 'Retaining blocks', 'Gravel & pebbles', 'Edging'],
      icon: 'ri-stack-line'
    },
    {
      category: 'Soil & Mulch',
      items: ['Garden soil', 'Potting mix', 'Bark mulch', 'Compost'],
      icon: 'ri-seedling-line'
    },
    {
      category: 'Irrigation',
      items: ['Sprinkler heads', 'Drip systems', 'Controllers', 'Pipes & fittings'],
      icon: 'ri-drop-line'
    }
  ];

  const caseStudies = [
    {
      title: 'Residential Landscaping Business Growth',
      landscaper: 'Green Dreams Landscaping Sydney',
      challenge:
        'Struggled to provide quick, accurate quotes for complete garden makeovers. Lost jobs to competitors with faster turnaround times.',
      solution:
        'Implemented automated landscaping quoting with plant database integration and real-time material pricing from nurseries.',
      results: [
        'Quote generation time reduced from 4 hours to 30 minutes',
        'Garden makeover bookings increased by 340%',
        'Average job value increased from $8,000 to $12,500',
        'Customer satisfaction score improved to 4.9/5'
      ],
      image:
        'https://readdy.ai/api/search-image?query=stunning%20luxury%20residential%20garden%20transformation%20with%20professional%20landscaping%20beautiful%20native%20plants%20premium%20retaining%20wall%20and%20paved%20pathway%20modern%20australian%20home%20backyard%20bright%20sunny%20day%20showing%20quality%20design%20and%20expert%20craftsmanship%20cinematic%20lighting&width=800&height=500&seq=land-case-premium-1&orientation=landscape'
    },
    {
      title: 'Commercial Maintenance Contract Success',
      landscaper: 'Metro Green Services Melbourne',
      challenge:
        'Managing quotes for commercial maintenance contracts was time-consuming. Tracking multiple properties and services led to billing errors.',
      solution:
        'Adopted comprehensive quoting system with recurring service scheduling and automated invoicing.',
      results: [
        'Won 12 major commercial contracts worth $450K annually',
        'Billing accuracy improved by 98%',
        'Contract renewal rate increased to 95%',
        'Profit margins increased by 22%'
      ],
      image:
        'https://readdy.ai/api/search-image?query=professional%20commercial%20landscaping%20maintenance%20team%20working%20on%20luxury%20corporate%20building%20grounds%20with%20modern%20equipment%20and%20irrigation%20system%20showing%20well%20maintained%20lawns%20and%20garden%20beds%20bright%20natural%20lighting%20quality%20workmanship%20cinematic%20photography&width=800&height=500&seq=land-case-premium-2&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Floating Callback Button */}
      <button
        onClick={() => setShowCallbackForm(true)}
        className="fixed left-6 bottom-6 z-40 flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-full shadow-2xl hover:shadow-emerald-500/30 hover:scale-105 transition-all duration-300 cursor-pointer group"
      >
        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
          <i className="ri-phone-line text-xl"></i>
        </div>
        <div className="pr-2">
          <div className="font-semibold text-sm whitespace-nowrap">Request Callback</div>
          <div className="text-emerald-100 text-xs whitespace-nowrap">We'll call you back</div>
        </div>
      </button>

      {/* Callback Request Modal */}
      {showCallbackForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-300">
            <button
              onClick={() => setShowCallbackForm(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer z-10"
            >
              <i className="ri-close-line text-xl text-gray-600"></i>
            </button>

            <div className="bg-gradient-to-br from-emerald-600 to-green-600 p-8 text-white">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-4">
                <i className="ri-phone-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-2">Request a Callback</h3>
              <p className="text-emerald-100">
                Leave your details and we'll call you back within 2 hours during business hours.
              </p>
            </div>

            <form
              id="landscaping-callback-form"
              data-readdy-form
              onSubmit={handleCallbackSubmit}
              className="p-8 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Name *</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={callbackData.name}
                  onChange={e => setCallbackData({ ...callbackData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={callbackData.phone}
                  onChange={e => setCallbackData({ ...callbackData, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm"
                  placeholder="0412 345 678"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={callbackData.email}
                  onChange={e => setCallbackData({ ...callbackData, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm"
                  placeholder="john@email.com (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Call Time</label>
                <select
                  name="preferredTime"
                  value={callbackData.preferredTime}
                  onChange={e => setCallbackData({ ...callbackData, preferredTime: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="morning">Morning (9am - 12pm)</option>
                  <option value="afternoon">Afternoon (12pm - 5pm)</option>
                  <option value="evening">Evening (5pm - 7pm)</option>
                  <option value="anytime">Anytime</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">What can we help with?</label>
                <textarea
                  name="message"
                  value={callbackData.message}
                  onChange={e =>
                    setCallbackData({ ...callbackData, message: e.target.value.slice(0, 500) })
                  }
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm resize-none"
                  placeholder="Tell us briefly what you'd like to discuss..."
                />
                <div className="text-xs text-gray-400 text-right mt-1">
                  {callbackData.message.length}/500
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl font-semibold hover:from-emerald-700 hover:to-green-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-phone-line"></i>
                    Request Callback
                  </span>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Callback Success Toast */}
      {callbackSubmitted && (
        <div className="fixed bottom-6 left-6 z-50 bg-emerald-600 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-bottom duration-300">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <i className="ri-check-line text-2xl"></i>
          </div>
          <div>
            <div className="font-bold">Callback Requested!</div>
            <div className="text-emerald-100 text-sm">We'll call you back soon</div>
          </div>
        </div>
      )}

      {/* Floating Lead Capture Form */}
      {showLeadForm && !formSubmitted && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="relative bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in duration-300">
            <button
              onClick={() => setShowLeadForm(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl text-gray-600"></i>
            </button>

            <div className="bg-gradient-to-br from-emerald-600 to-green-600 p-8 text-white">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-full text-sm font-medium mb-4">
                <i className="ri-gift-line"></i>
                Limited Time Offer
              </div>
              <h3 className="text-2xl font-bold mb-2">Get Your Free Landscaping Business Audit</h3>
              <p className="text-emerald-100">
                Plus 30-day extended trial when you book a demo today
              </p>
            </div>

            <form
              id="landscaping-lead-form"
              data-readdy-form
              onSubmit={handleFormSubmit}
              className="p-8 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Name</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm"
                  placeholder="john@landscaping.com.au"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm"
                  placeholder="0412 345 678"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Specialization</label>
                  <select
                    name="businessType"
                    value={formData.businessType}
                    onChange={e => setFormData({ ...formData, businessType: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm cursor-pointer"
                  >
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                    <option value="hardscape">Hardscaping</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Team Size</label>
                  <select
                    name="teamSize"
                    value={formData.teamSize}
                    onChange={e => setFormData({ ...formData, teamSize: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm cursor-pointer"
                  >
                    <option value="1-2">Just me / 1-2</option>
                    <option value="3-5">3-5 people</option>
                    <option value="6-10">6-10 people</option>
                    <option value="10+">10+ people</option>
                  </select>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl font-semibold hover:from-emerald-700 hover:to-green-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-calendar-check-line"></i>
                    Get My Free Audit + Demo
                  </span>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Sticky Bottom CTA Bar */}
      {showStickyBar && !formSubmitted && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-emerald-600 to-green-600 shadow-2xl shadow-black/20 transform transition-transform duration-300">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-full">
                <i className="ri-plant-fill text-white"></i>
                <span className="text-white text-sm font-medium">Landscapers Only</span>
              </div>
              <div className="text-white">
                <div className="font-bold text-lg">Get 30% Off Your First 3 Months</div>
                <div className="text-emerald-100 text-sm hidden sm:block">
                  Plus free business audit worth $499
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowLeadForm(true)}
                className="px-6 py-3 bg-white text-emerald-600 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg cursor-pointer whitespace-nowrap"
              >
                <i className="ri-gift-line mr-2"></i>
                Claim Offer
              </button>
              <button
                onClick={() => setShowStickyBar(false)}
                className="p-2 text-white/80 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Toast */}
      {formSubmitted && (
        <div className="fixed bottom-6 right-6 z-50 bg-emerald-600 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-bottom duration-300">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <i className="ri-check-line text-2xl"></i>
          </div>
          <div>
            <div className="font-bold">Thanks! We'll be in touch soon</div>
            <div className="text-emerald-100 text-sm">Check your email for confirmation</div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-green-50 to-lime-50"></div>
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-green-500 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 rounded-full mb-6">
                <i className="ri-plant-fill text-emerald-600"></i>
                <span className="text-sm font-medium text-emerald-900">
                  For Professional Landscapers
                </span>
              </div>

              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Landscaping Quoting
                <br />
                <span className="text-emerald-600">Made Simple</span>
              </h1>

              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Generate professional landscaping quotes in minutes. From garden designs to hardscaping,
                streamline your quoting process and win more jobs with accurate, detailed estimates.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <Link
                  to="/demo"
                  className="px-8 py-4 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  Book Free Demo
                </Link>
                <Link
                  to="/invoice-demo"
                  className="px-8 py-4 bg-white text-emerald-600 rounded-lg font-semibold border-2 border-emerald-600 hover:bg-emerald-50 transition-all duration-300 whitespace-nowrap"
                >
                  Try Quote Generator
                </Link>
              </div>

              <div className="flex items-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <i className="ri-time-line text-emerald-600"></i>
                  <span>
                    <strong className="text-gray-900">4.5 min</strong> avg quote time
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-emerald-600"></i>
                  <span>Plant Database Included</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-green-500 rounded-3xl transform rotate-3"></div>
              <img
                src="https://readdy.ai/api/search-image?query=professional%20australian%20landscaper%20in%20beautiful%20luxury%20garden%20setting%20planting%20premium%20native%20shrubs%20and%20flowers%20wearing%20quality%20work%20clothes%20and%20gloves%20with%20professional%20landscaping%20tools%20lush%20green%20plants%20and%20modern%20sophisticated%20outdoor%20design%20natural%20lighting%20cinematic%20quality&width=600&height=700&seq=land-hero-premium-1&orientation=portrait"
                alt="Professional Landscaper"
                className="relative rounded-3xl shadow-2xl w-full h-auto object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 max-w-xs">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-2xl text-green-600"></i>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">$15,200</div>
                    <div className="text-sm text-gray-600">Garden quote sent in 18 min</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-emerald-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${stat.icon} text-3xl text-white`}></i>
                </div>
                <div className="text-4xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-emerald-200">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Landscaping Trade Features</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to quote landscaping jobs quickly and accurately
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group bg-white border-2 border-gray-100 rounded-2xl p-8 hover:border-emerald-600 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-16 h-16 bg-emerald-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-emerald-600 transition-colors duration-300">
                  <i className={`${feature.icon} text-3xl text-emerald-600 group-hover:text-white transition-colors duration-300`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{feature.description}</p>
                <div className="text-emerald-600 font-semibold">{feature.price}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supplier Pricing Integration */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Real-Time Plant & Material Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Integrated pricing from major Australian nurseries and suppliers
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {supplierPricing.map((category, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border-2 border-emerald-100 shadow-lg">
                <div className="w-12 h-12 bg-emerald-600 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${category.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">{category.category}</h3>
                <ul className="space-y-2">
                  {category.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <i className="ri-check-line text-emerald-600 mt-0.5"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-6">Integrated with leading suppliers:</p>
            <div className="flex flex-wrap justify-center gap-8 items-center">
              <div className="text-2xl font-bold text-gray-400">Bunnings</div>
              <div className="text-2xl font-bold text-gray-400">Flower Power</div>
              <div className="text-2xl font-bold text-gray-400">Nursery Network</div>
              <div className="text-2xl font-bold text-gray-400">Landscape Supply</div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Trusted by Landscapers Across Australia
            </h2>
            <p className="text-xl text-gray-600">See how landscapers are growing their businesses</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center gap-4 mb-6">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.business}</div>
                    <div className="text-xs text-emerald-600 font-medium">{testimonial.specialty}</div>
                  </div>
                </div>

                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-amber-400"></i>
                  ))}
                </div>

                <p className="text-gray-700 mb-4 leading-relaxed">"{testimonial.quote}"</p>

                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-sm font-semibold text-green-600">
                    <i className="ri-arrow-up-line"></i>
                    {testimonial.results}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Landscaping Business Success Stories
            </h2>
            <p className="text-xl text-gray-600">
              Real results from landscapers who transformed their businesses
            </p>
          </div>

          <div className="space-y-16">
            {caseStudies.map((study, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-emerald-50 rounded-3xl overflow-hidden shadow-xl"
              >
                <div className="grid lg:grid-cols-2 gap-8">
                  <div className="p-12">
                    <div className="inline-block px-4 py-2 bg-emerald-600 text-white rounded-full text-sm font-semibold mb-6">
                      Case Study
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">{study.title}</h3>
                    <div className="text-emerald-600 font-semibold mb-6">{study.landscaper}</div>

                    <div className="space-y-6">
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <i className="ri-error-warning-line text-red-500"></i>
                          Challenge
                        </h4>
                        <p className="text-gray-700">{study.challenge}</p>
                      </div>

                      <div>
                        <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <i className="ri-lightbulb-line text-emerald-600"></i>
                          Solution
                        </h4>
                        <p className="text-gray-700">{study.solution}</p>
                      </div>

                      <div>
                        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <i className="ri-trophy-line text-green-600"></i>
                          Results
                        </h4>
                        <ul className="space-y-2">
                          {study.results.map((result, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-gray-700">
                              <i className="ri-check-line text-green-600 mt-1"></i>
                              <span>{result}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="relative h-full min-h-[400px]">
                    <img
                      src={study.image}
                      alt={study.title}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-green-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Landscaping Business?
          </h2>
          <p className="text-xl text-emerald-100 mb-8">
            Join 3,600+ landscapers who are quoting faster, winning more jobs, and growing their revenue
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <Link
              to="/demo"
              className="px-8 py-4 bg-white text-emerald-600 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Book Your Free Demo
            </Link>
            <Link
              to="/invoice-demo"
              className="px-8 py-4 bg-emerald-700 text-white rounded-lg font-semibold hover:bg-emerald-800 transition-all duration-300 border-2 border-white whitespace-nowrap"
            >
              Try Quote Generator
            </Link>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-emerald-100">
            <div className="flex items-center gap-2">
              <i className="ri-check-line"></i>
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-check-line"></i>
              <span>14-day free trial</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-check-line"></i>
              <span>Cancel anytime</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
